import { apply3DRotationTo2D } from './geometryUtils';

// apply3DRotationTo2D(rotation, [1,0,0]) returns [dx, dz] — the normalized
// in-plane unit vector for the wall's long axis after Y-rotation.
// For rotation.y=0: [1,0] (runs along +X).
// For rotation.y=π/2: [0,-1] (runs along -Z, i.e. along the Z axis by magnitude).

const EPS = 1e-6;

export const getStructureWallAxis = (structureWall) => {
  const dir2d = apply3DRotationTo2D(structureWall.rotation);
  return {
    origin: { x: structureWall.center.x, z: structureWall.center.z },
    dir: { x: dir2d[0], z: dir2d[1] },
  };
};

export const projectOntoAxis = (point, axis) =>
  (point.x - axis.origin.x) * axis.dir.x +
  (point.z - axis.origin.z) * axis.dir.z;

export const roomWallsForStructureWall = (rooms, structureWallId) => {
  const out = [];
  for (const room of rooms || []) {
    for (const wall of room.walls || []) {
      if (wall?.alignment?.structureWallId === structureWallId)
        out.push({ room, wall });
    }
  }
  return out;
};

export const roomWallAxisSpan = (wall, axis) => {
  const bl = wall.alignment?.bottomLeft;
  const br = wall.alignment?.bottomRight;
  if (!bl || !br) return null;
  const t0 = projectOntoAxis(bl, axis);
  const t1 = projectOntoAxis(br, axis);
  return { min: Math.min(t0, t1), max: Math.max(t0, t1) };
};

export const spanToOpening = (structureWall, axis, span) => {
  const tMid = (span.min + span.max) / 2;
  const length = span.max - span.min;
  return {
    center: {
      x: axis.origin.x + axis.dir.x * tMid,
      y: structureWall.center.y,
      z: axis.origin.z + axis.dir.z * tMid,
    },
    dimensions: {
      x: length,
      y: structureWall.dimensions.y,
      z: structureWall.dimensions.z,
    },
    rotation: { ...structureWall.rotation },
    parentWallIds: [structureWall.id],
  };
};

/**
 * The clicked room's wall span on the structure wall under the cursor, as a
 * demoed-opening descriptor sized to that room wall's FULL extent. Returns null
 * if the click isn't on any room wall of this structure wall. Neighbor/both-room
 * handling is done downstream by the existing _isStructureWallSnap placement path
 * (which clips each room copy to its own wall span). Picks the smallest containing
 * span on ties (robust to overlapping candidates).
 */
export const computeClickedRoomWallSpan = (snapResult, rooms) => {
  const structureWall = snapResult?.wall;
  const click = snapResult?.position;
  if (!structureWall?.id || !click) return null;
  const axis = getStructureWallAxis(structureWall);
  const clickT = projectOntoAxis(click, axis);
  const candidates = roomWallsForStructureWall(rooms, structureWall.id);
  let best = null;
  for (const { wall } of candidates) {
    const span = roomWallAxisSpan(wall, axis);
    if (!span) continue;
    if (clickT >= span.min - EPS && clickT <= span.max + EPS) {
      if (!best || span.max - span.min < best.max - best.min) best = span;
    }
  }
  if (!best) return null;
  return spanToOpening(structureWall, axis, best);
};
